import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import { Lock, OpenInNew } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';

interface CompleteSessionDialogProps {
  open: boolean;
  session: Session | null;
  onClose: () => void;
  onCompleted: (session: Session) => void;
}

const CompleteSessionDialog: React.FC<CompleteSessionDialogProps> = ({
  open, session, onClose, onCompleted
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [blockReason, setBlockReason] = useState<string | null>(null);
  const [stillEnrolled, setStillEnrolled] = useState<number>(0);

  const reset = () => { setBlockReason(null); setStillEnrolled(0); };

  const handleConfirm = async () => {
    if (!session) return;
    setSubmitting(true);
    reset();
    try {
      const updated = await sessionsApi.complete(session._id);
      enqueueSnackbar(`"${updated.name}" marked complete`, { variant: 'success' });
      onCompleted(updated);
      onClose();
    } catch (err: any) {
      if (err?.statusCode === 422 || err?.status === 422) {
        const remaining = err?.details?.stillEnrolled ?? 0;
        setStillEnrolled(remaining);
        setBlockReason(
          `${remaining} student${remaining === 1 ? '' : 's'} ${remaining === 1 ? 'is' : 'are'} still enrolled in this session.`
        );
      } else {
        enqueueSnackbar(err?.message || 'Could not complete session', { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startPromotion = () => {
    if (!session) return;
    onClose();
    // The wizard route will offer "start a run for this source session" if none exists.
    navigate(`/app/promotions/new?sourceSessionId=${session._id}`);
  };

  if (!session) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth TransitionProps={{ onExited: reset }}>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Lock fontSize="small" color="action" />
          <Typography variant="h6" fontWeight={600}>Complete session</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Locks <strong>{session.name}</strong>. Enrollments in this session become read-only.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {blockReason ? (
          <Alert severity="warning" variant="outlined">
            <AlertTitle sx={{ fontWeight: 600 }}>Promote or exit those students first</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1.5 }}>{blockReason}</Typography>
            <Typography variant="body2" color="text.secondary">
              Run the promotion workflow to move them forward, or mark them as transferred/dropped from the Enrollments page.
              The session can be completed once every student has a terminal status.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                onClick={startPromotion}
                variant="contained"
                size="small"
                disableElevation
                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
              >
                Start promotion workflow
              </Button>
              {stillEnrolled > 0 && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    onClose();
                    navigate(`/app/enrollments?sessionId=${session._id}&status=enrolled`);
                  }}
                >
                  View {stillEnrolled} pending
                </Button>
              )}
            </Stack>
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Once completed, this session moves to read-only. You can still view its enrollments, results, and reports.
            </Typography>
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                The session will be blocked from completion if any student is still <strong>Enrolled</strong>.
                Promote or exit them first.
              </Typography>
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>{blockReason ? 'Close' : 'Cancel'}</Button>
        {!blockReason && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={submitting}
            disableElevation
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Completing…' : 'Mark as completed'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CompleteSessionDialog;
